import React from 'react';
import {
  Form,
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useOutlet,
  useSubmit
} from 'react-router-dom';
import * as Headless from '@headlessui/react';

import { Await } from '@oysterjs/uiv2/async';
import {
  AgencySubscriptionFieldsFragment,
  AgencyUserFieldsFragment,
  GetSubscriptionUpdateLinkQueryVariables,
  GetSubscriptionUpdateLinkDocument,
  GetSubscriptionUpdateLinkQuery,
  LoggedInAgencyUserFieldsFragment,
  SubscriptionState,
  SubscriptionPlan,
  SubscriptionPeriod,
  GetSubscriptionUpdatePaymentLinkQueryVariables,
  GetSubscriptionUpdatePaymentLinkDocument,
  GetSubscriptionUpdatePaymentLinkQuery,
  AgencyUserState,
  AgencyUserInput,
  CreateAgencyUserMutation,
  DisableAgencyUserMutation,
  DisableAgencyUserMutationVariables,
  DisableAgencyUserDocument,
  EnableAgencyUserMutation,
  EnableAgencyUserMutationVariables,
  EnableAgencyUserDocument
} from '../../api/graphql';
import { Spinner } from '@oysterjs/uiv2/spinner';
import { Button } from '@oysterjs/uiv2/button';
import { Badge } from '@oysterjs/uiv2/badge';
import { client } from '../../api/client';
import {
  GetSubscriptionBillingPortalLinkDocument,
  GetSubscriptionBillingPortalLinkQuery,
  GetSubscriptionBillingPortalLinkQueryVariables
} from '../../api/graphql';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  MountableDialog
} from '@oysterjs/uiv2/dialog';
import { Description, ErrorMessage, FieldGroup } from '@oysterjs/uiv2/fieldset';
import { Field } from '@oysterjs/uiv2/fieldset';
import { Label } from '@oysterjs/uiv2/fieldset';
import { Fieldset } from '@oysterjs/uiv2/fieldset';
import { Input } from '@oysterjs/uiv2/input';
import { ActionResult } from '@oysterjs/corev2/graphql/actions';
import { ValidationError } from '@oysterjs/corev2/graphql';
import { useFormActions } from '@oysterjs/corev2/graphql/form';
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';

export const SettingsSubscriptionPage = () => {
  const outlet = useOutlet();
  const location = useLocation();
  const loader = useLoaderData() as {
    data: Promise<{
      agencyUsers?: AgencyUserFieldsFragment[];
      agencyUser?: LoggedInAgencyUserFieldsFragment;
    }>;
  };

  const [disableUserId, setDisableUserId] = React.useState<string | null>(null);
  const [enableUserId, setEnableUserId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [, setError] = React.useState<string | null>(null);

  const onManageSubscription = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await client.query<
        GetSubscriptionBillingPortalLinkQuery,
        GetSubscriptionBillingPortalLinkQueryVariables
      >({
        query: GetSubscriptionBillingPortalLinkDocument
      });

      if (data.errors?.length) {
        setError(data.errors[0].message);
      } else if (!data.data?.subscriptionManagementLinks?.billingPortalUrl) {
        setError('No billing portal URL found');
      } else {
        // Open link in new tab
        window.open(data.data.subscriptionManagementLinks.billingPortalUrl, '_blank');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <React.Suspense fallback={<SubscriptionPageLoader />}>
        <Await resolve={loader.data}>
          {(data) =>
            data.agencyUser?.agency.subscription && (
              <div className="space-y-6">
                <SubscriptionStatusBanner subscription={data.agencyUser?.agency.subscription} />
                <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium m-0 p-0 text-neutral-900">
                        Your Subscription
                      </h3>
                      <Button color="sky" loading={loading} onClick={onManageSubscription}>
                        Manage
                      </Button>
                    </div>
                  </div>
                  <div className="px-4 py-5 sm:px-6">
                    <h4 className="text-base/6 font-medium mt-0 pt-0 font-body text-neutral-900">
                      {getPlanName(
                        data.agencyUser.agency.subscription.plan ?? SubscriptionPlan.Standard,
                        data.agencyUser.agency.subscription.period ?? SubscriptionPeriod.Monthly,
                        data.agencyUser.agency.subscription.numSeats ?? 0
                      )}
                      <span className="ml-2">
                        <SubscriptionStateBadge state={data.agencyUser.agency.subscription.state} />
                      </span>
                    </h4>
                    <div className="w-full h-4 bg-neutral-100 rounded-md mt-4">
                      <motion.div
                        animate={{
                          width: `${Math.min(1, data.agencyUser.agency.subscription.numSeatsActive / data.agencyUser.agency.subscription.numSeats) * 100}%`
                        }}
                        transition={{ duration: 0.5, ease: 'easeInOut' }}
                        className={clsx(
                          'w-0 h-full bg-gradient-to-r from-sky-600 to-sky-500 rounded-l-md',
                          data.agencyUser.agency.subscription.numSeatsActive >=
                            data.agencyUser.agency.subscription.numSeats && 'rounded-r-md'
                        )}
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-8 mt-2">
                      <div className="flex items-center gap-2">
                        <div className="size-2 bg-sky-500 ring-1 ring-sky-600 rounded-full" />
                        <p className="text-sm text-neutral-500">
                          {data.agencyUser.agency.subscription.numSeatsActive} seat
                          {data.agencyUser.agency.subscription.numSeatsActive === 1 ? '' : 's'}{' '}
                          assigned
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="size-2 bg-neutral-100 ring-1 ring-neutral-200 rounded-full" />
                        <p className="text-sm text-neutral-500">
                          {data.agencyUser.agency.subscription.numSeats -
                            data.agencyUser.agency.subscription.numSeatsActive}{' '}
                          seat
                          {data.agencyUser.agency.subscription.numSeats -
                            data.agencyUser.agency.subscription.numSeatsActive ===
                          1
                            ? ''
                            : 's'}{' '}
                          available
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-neutral-200 rounded-lg bg-white shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium m-0 p-0 text-neutral-900">Manage Users</h3>
                      <Button
                        color="sky"
                        disabled={
                          data.agencyUser?.agency.subscription.numSeatsActive >=
                          data.agencyUser?.agency.subscription.numSeats
                        }
                        href="/app/settings/subscription/users"
                        className={clsx(
                          data.agencyUser?.agency.subscription.numSeatsActive >=
                            data.agencyUser?.agency.subscription.numSeats
                            ? 'opacity-50 cursor-default'
                            : ''
                        )}
                      >
                        Add User
                      </Button>
                    </div>
                  </div>
                  <div className="py-5">
                    <ul role="list" className="divide-y divide-neutral-200">
                      {[...(data.agencyUsers || [])]
                        .sort((a, b) => a.state.localeCompare(b.state))
                        .map((user) => (
                          <li
                            key={user.id}
                            className="flex justify-between px-4 py-4 sm:px-6 first:pt-0 last:pb-0"
                          >
                            <div className="flex min-w-0 gap-x-4">
                              <div className="size-12 flex-none rounded-full bg-neutral-100 flex items-center justify-center text-neutral-700 font-semibold text-lg">
                                {user.name
                                  .split(' ')
                                  .map((name) => name[0])
                                  .join('')
                                  .toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-auto">
                                <p className="text-sm/6 font-semibold text-neutral-900">
                                  {user.name}
                                  <span className="ml-2">
                                    <UserStateBadge state={user.state} />
                                  </span>
                                </p>
                                <p className="flex text-xs/5 text-neutral-500">{user.email}</p>
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-x-6">
                              <Headless.Menu as="div" className="relative flex-none">
                                <Headless.MenuButton className="-m-2.5 block p-2.5 text-neutral-500 hover:text-neutral-900">
                                  <span className="sr-only">Open options</span>
                                  <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
                                </Headless.MenuButton>
                                <Headless.MenuItems
                                  transition
                                  className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-neutral-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                                >
                                  {user.state === AgencyUserState.Active && (
                                    <Headless.MenuItem>
                                      <span
                                        role="button"
                                        onClick={() => setDisableUserId(user.id)}
                                        className="block px-3 py-1 text-sm/6 text-neutral-900 data-[focus]:bg-neutral-50 data-[focus]:outline-none"
                                      >
                                        Disable<span className="sr-only">, {user.name}</span>
                                      </span>
                                    </Headless.MenuItem>
                                  )}
                                  {user.state === AgencyUserState.Disabled && (
                                    <Headless.MenuItem>
                                      <span
                                        role="button"
                                        onClick={() => setEnableUserId(user.id)}
                                        className="block px-3 py-1 text-sm/6 text-neutral-900 data-[focus]:bg-neutral-50 data-[focus]:outline-none"
                                      >
                                        Enable<span className="sr-only">, {user.name}</span>
                                      </span>
                                    </Headless.MenuItem>
                                  )}
                                </Headless.MenuItems>
                              </Headless.Menu>
                            </div>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
            )
          }
        </Await>
      </React.Suspense>
      <AnimatePresence mode="wait">
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
      <DisableUserDialog
        open={!!disableUserId}
        userId={disableUserId}
        onClose={() => setDisableUserId(null)}
      />
      <EnableUserDialog
        open={!!enableUserId}
        userId={enableUserId}
        onClose={() => setEnableUserId(null)}
      />
    </>
  );
};

const SubscriptionPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900">Loading settings...</h3>
        <p className="mt-1 text-sm text-neutral-500">This might take a moment.</p>
      </div>
    </div>
  );
};

export const AddUserDialog = () => {
  const submit = useSubmit();
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = useNavigation();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const action = useActionData() as ActionResult<CreateAgencyUserMutation>;
  const [form, setForm] = React.useState<AgencyUserInput>({ name: '', email: '' });

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    submit(form, { method: 'POST', encType: 'application/json' });
  };

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/settings/subscription');
    } else {
      navigate(-1);
    }
  };

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (action?.applicationError) {
      setError(action.applicationError.message);
    }
  }, [action?.applicationError]);

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction === navigation.location?.pathname) {
      setLoading(false);
      if (!action?.validationErrors && !action?.applicationError) {
        setError(null);
        onClose();
      }
    }
  }, [
    navigation.state,
    navigation.formAction,
    navigation.location?.pathname,
    action?.validationErrors,
    action?.applicationError
  ]);

  return (
    <MountableDialog onClose={onClose}>
      <DialogTitle>Add User</DialogTitle>
      <DialogDescription>
        Add a new user to your agency. Once you add them, they'll be able to sign in and use Cara.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Description>Enter the name of the user you want to add.</Description>
                <Input
                  placeholder="John Smith"
                  type="text"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.currentTarget.value;
                    updateForm(['name'], (prev) => ({ ...prev, name }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['name'])}
                />
                {!!getValidationError(validationErrors, ['name']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['name'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Email Address</Label>
                <Description>Enter the email address of the user you want to add.</Description>
                <Input
                  placeholder="john.smith@acme.com"
                  type="email"
                  value={form.email}
                  onChange={(e) => {
                    const email = e.currentTarget.value;
                    updateForm(['email'], (prev) => ({ ...prev, email }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['email'])}
                />
                {!!getValidationError(validationErrors, ['email']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['email'])}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        {error && <ErrorMessage className="mt-4">{error}</ErrorMessage>}
        <DialogActions>
          <Button color="white" onClick={onClose}>
            Cancel
          </Button>
          <Button color="sky" type="submit" loading={loading}>
            Add User
          </Button>
        </DialogActions>
      </Form>
    </MountableDialog>
  );
};

export const DisableUserDialog = (props: {
  open: boolean;
  userId: string | null;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const navigation = useNavigation();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const action = useActionData() as ActionResult<DisableAgencyUserMutation>;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await client.mutate<
        DisableAgencyUserMutation,
        DisableAgencyUserMutationVariables
      >({
        mutation: DisableAgencyUserDocument,
        variables: { id: props.userId || '' }
      });

      if (res.errors?.length) {
        setError(res.errors[0].message);
      } else {
        onClose();
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    if (!props.open) {
      return;
    }
    setError(null);
    props.onClose();
    navigate('/app/settings/subscription');
  };

  React.useEffect(() => {
    if (action?.applicationError) {
      setError(action.applicationError.message);
    }
  }, [action?.applicationError]);

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction === navigation.location?.pathname) {
      setLoading(false);
      if (!action?.validationErrors && !action?.applicationError) {
        onClose();
      }
    }
  }, [
    navigation.state,
    navigation.formAction,
    navigation.location?.pathname,
    action?.validationErrors,
    action?.applicationError
  ]);

  return (
    <Dialog open={props.open} onClose={onClose}>
      <DialogTitle>Disable User</DialogTitle>
      <DialogDescription>
        Are you sure you want to disable this user's access to Cara? This will reduce the number of
        seats allocated, but will prevent the user from signing in and using Cara.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        {error && <ErrorMessage className="mt-2">{error}</ErrorMessage>}
        <DialogActions>
          <Button color="white" onClick={onClose}>
            Cancel
          </Button>
          <Button color="red" type="submit" loading={loading}>
            Disable User
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export const EnableUserDialog = (props: {
  open: boolean;
  userId: string | null;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const navigation = useNavigation();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const action = useActionData() as ActionResult<EnableAgencyUserMutation>;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await client.mutate<EnableAgencyUserMutation, EnableAgencyUserMutationVariables>({
        mutation: EnableAgencyUserDocument,
        variables: { id: props.userId || '' }
      });

      if (res.errors?.length) {
        setError(res.errors[0].message);
      } else {
        onClose();
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    if (!props.open) {
      return;
    }
    setError(null);
    props.onClose();
    navigate('/app/settings/subscription');
  };

  React.useEffect(() => {
    if (action?.applicationError) {
      setError(action.applicationError.message);
    }
  }, [action?.applicationError]);

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction === navigation.location?.pathname) {
      setLoading(false);
      if (!action?.validationErrors && !action?.applicationError) {
        onClose();
      }
    }
  }, [
    navigation.state,
    navigation.formAction,
    navigation.location?.pathname,
    action?.validationErrors,
    action?.applicationError
  ]);

  return (
    <Dialog open={props.open} onClose={onClose}>
      <DialogTitle>Enable User</DialogTitle>
      <DialogDescription>
        Are you sure you want to enable this user's access to Cara? This will increase the number of
        seats allocated, and will allow the user to sign in and use Cara. If you do not have enough
        seats left, you will need to update your subscription to add at least one seat.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        {error && <ErrorMessage className="mt-2">{error}</ErrorMessage>}
        <DialogActions>
          <Button color="white" onClick={onClose}>
            Cancel
          </Button>
          <Button color="sky" type="submit" loading={loading}>
            Enable User
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export const SubscriptionStatusBanner = ({
  subscription
}: {
  subscription: AgencySubscriptionFieldsFragment;
}) => {
  const [loading, setLoading] = React.useState(false);
  const [, setError] = React.useState<string | null>(null);

  const onManageSubscription = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await client.query<
        GetSubscriptionBillingPortalLinkQuery,
        GetSubscriptionBillingPortalLinkQueryVariables
      >({
        query: GetSubscriptionBillingPortalLinkDocument
      });

      if (data.errors?.length) {
        setError(data.errors[0].message);
      } else if (!data.data?.subscriptionManagementLinks?.billingPortalUrl) {
        setError('No billing portal URL found');
      } else {
        // Open link in new tab
        window.open(data.data.subscriptionManagementLinks.billingPortalUrl, '_blank');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const onUpgrade = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await client.query<
        GetSubscriptionUpdateLinkQuery,
        GetSubscriptionUpdateLinkQueryVariables
      >({
        query: GetSubscriptionUpdateLinkDocument
      });

      if (data.errors?.length) {
        setError(data.errors[0].message);
      } else if (!data.data?.subscriptionManagementLinks?.updateSubscriptionUrl) {
        setError('No subscription update URL found');
      } else {
        window.open(data.data.subscriptionManagementLinks.updateSubscriptionUrl, '_blank');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const onUpdatePayment = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await client.query<
        GetSubscriptionUpdatePaymentLinkQuery,
        GetSubscriptionUpdatePaymentLinkQueryVariables
      >({
        query: GetSubscriptionUpdatePaymentLinkDocument
      });

      if (data.errors?.length) {
        setError(data.errors[0].message);
      } else if (!data.data?.subscriptionManagementLinks?.updatePaymentMethodUrl) {
        setError('No subscription update payment URL found');
      } else {
        window.open(data.data.subscriptionManagementLinks.updatePaymentMethodUrl, '_blank');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (subscription.state === SubscriptionState.Trial) {
    return (
      <div className="overflow-hidden rounded-lg bg-gradient-to-r from-sky-700 to-sky-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
              Your trial is ending soon
            </h3>
            <Button color="white" loading={loading} onClick={onManageSubscription}>
              Upgrade
            </Button>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-neutral-200">
            Your trial will end on{' '}
            <strong>
              {new Date(subscription.trialEndDate ?? new Date())?.toLocaleDateString()}
            </strong>
            . Please upgrade to a paid plan to continue using Cara.
          </p>
        </div>
      </div>
    );
  }

  if (subscription.state === SubscriptionState.Overdue) {
    return (
      <div className="overflow-hidden rounded-lg bg-gradient-to-r from-red-700 to-red-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
              Your subscription payment is overdue
            </h3>
            <Button color="white" loading={loading} onClick={onUpdatePayment}>
              Update Payment
            </Button>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-neutral-200">
            Please make sure your payment method is up to date in order to continue using Cara.
          </p>
        </div>
      </div>
    );
  }

  if (subscription.state === SubscriptionState.Expired) {
    return (
      <div className="overflow-hidden rounded-lg bg-gradient-to-r from-neutral-700 to-neutral-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
              Your subscription has expired
            </h3>
            <Button color="white" loading={loading} onClick={onManageSubscription}>
              Restart
            </Button>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-neutral-200">
            Please restart your subscription to continue using Cara.
          </p>
        </div>
      </div>
    );
  }

  if (subscription.state === SubscriptionState.Paused) {
    return (
      <div className="overflow-hidden rounded-lg bg-gradient-to-r from-amber-700 to-amber-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
              Your subscription is paused
            </h3>
            <Button color="white" loading={loading} onClick={onUpgrade}>
              Resume
            </Button>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-neutral-200">
            Resume your subscription to continue using Cara.
          </p>
        </div>
      </div>
    );
  }
};

const SubscriptionStateBadge = ({ state }: { state: SubscriptionState }) => {
  switch (state) {
    case SubscriptionState.Trial:
      return <Badge color="sky">Trial</Badge>;
    case SubscriptionState.Active:
      return <Badge color="green">Active</Badge>;
    case SubscriptionState.Expired:
      return <Badge color="neutral">Expired</Badge>;
    case SubscriptionState.Overdue:
      return <Badge color="red">Overdue</Badge>;
    case SubscriptionState.Paused:
      return <Badge color="amber">Paused</Badge>;
  }
};

const getPlanName = (plan: SubscriptionPlan, period: SubscriptionPeriod, seats: number) => {
  const periodName = period === SubscriptionPeriod.Monthly ? 'Monthly' : 'Annual';

  switch (plan) {
    case SubscriptionPlan.Standard:
      return `Cara Standard (${periodName}, ${seats} seats)`;
    case SubscriptionPlan.Enterprise:
      return `Cara Enterprise (${periodName}, ${seats} seats)`;
    default:
      return 'Unknown';
  }
};

const UserStateBadge = ({ state }: { state: AgencyUserState }) => {
  switch (state) {
    case AgencyUserState.Active:
      return <Badge color="sky">Active</Badge>;
    case AgencyUserState.Disabled:
      return <Badge color="neutral">Disabled</Badge>;
  }
};
